"use client";
import Image from "next/image";
import HighlightedText from "./HighlightedText";
import { getStrapiMedia } from "../lib/api-helpers";
import { useEffect, useLayoutEffect } from "react";
import ReactGA from "react-ga4";
import Breadcrumb from "./BreadCrumb";

interface Button {
  id: string;
  url: string;
  text: string;
  type: string;
  newTab: boolean;
}

interface Picture {
  data: {
    id: string;
    attributes: {
      url: string;
      name: string;
      alternativeText: string;
    };
  };
}

interface HeroProps {
  data: {
    id: string;
    title: string;
    direction: string;
    arcColor: string;
    description: string;
    image: Picture;
    breadcrumButtons: Button[];
  };
}

export default function ResourceHero({ data }: HeroProps) {
  const imgUrl = getStrapiMedia(data?.image?.data?.attributes?.url);

  function addStyle() {
    const htmlElement = document.documentElement;

    htmlElement.style.scrollSnapType = "y mandatory";
    htmlElement.style.scrollPaddingTop = "65px";
    htmlElement.style.scrollBehavior = "smooth";
    htmlElement.style.scrollSnapStop = "always";
  }

  function removeStyle() {  
    const htmlElement = document.documentElement;

    htmlElement.style.scrollSnapType = "";
    htmlElement.style.scrollPaddingTop = "";
    htmlElement.style.scrollBehavior = "";
    htmlElement.style.scrollSnapStop = "";
  }

  const getItemArrangment = (alt: any) => {
    switch (alt) {
      case "center":
        return "flex items-center justify-center flex-col items-end overflow-hidden";

      case "left":
        return "flex items-center justify-start flex-col md:flex-row items-center mt-[-15px] ";

      default:
        return "w-12 min-w-12  h-12  4xl:w-24 4xl:h-24";
    }
  };

  useLayoutEffect(() => {
    return () => {
      window.scrollTo(0, 0);
      removeStyle();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isTop = window.scrollY === 0;
      const isBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight;

      if (isTop || isBottom) {
        removeStyle();
      } else {
        addStyle();
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  useEffect(() => {
    const timeOut = setTimeout(() => {
      const measurementId = process.env.NEXT_PUBLIC_MEASUREMENT_ID;
      measurementId ? ReactGA.initialize(measurementId) : "";
      ReactGA.send({
        hitType: "pageview",
        page: "/qualitia-for-web-and-mobile",
        title: "Default",
      });
    }, 1000);
    return () => clearTimeout(timeOut);
  }, []);

  return (
    <section className='flex flex-col text-gray-600 body-font '>
      <h2 className='sr-only'>Resource Hero Section</h2>
      {data.breadcrumButtons && <div className="pl-2 z-[1] max-sm:mb-[20px]" style={{marginTop:"20px"}} ><Breadcrumb items={data.breadcrumButtons} /> </div>}
      <div
        className={`container items-center justify-center  mx-auto xl:px-0 px-6 ${getItemArrangment(
          data.direction
        )} ${data.breadcrumButtons.length > 0 ? "" : "md:mt-[-78px]"}`}>
        {data.title && <div
          className={`z-50 w-full text-${data.direction} max-sm:text-center flex flex-col items-start self-center xl:col-span-2"`}>
          <HighlightedText
            text={data.title}
            tag='h1'
            className={`!leading-[1.2] text-5xl 2xl:text-6xl mb-[16px]  w-full font-bold text-[#032d60]`}
          />
          <HighlightedText
            text={data.description}
            tag='p'
            className={`text-2xl text-[#032d60] ${
              data.direction === "left" ? "" : "mx-auto max-w-[360px] lg:max-w-full mb-[56px]"
            }`}
          />
        </div>}
        {imgUrl && <div
          className={`${
            data.direction === "left" ? "lg:mt-[0px] w-full max-w-[320px] md:max-w-[400px] lg:max-w-[380px] xl:max-w-[360px]  3xl:max-w-[480px] xl:right-[50px] 2xl:right-[0px] 3xl:right-[50px]" : data.breadcrumButtons.length > 0 ? "" : "md:mt-[48px] mt-auto"
          } pb-[53px]`}>
          <Image
            src={imgUrl ?? ""}
            alt={"Resources"}
            className={`mt-auto w-full z-50 object-contain object-center ${
              data.direction === "left"
                ? "w-full md:h-auto lg:h-[450px] xl:h-[420px]"
                : "lg:h-[500px] xl:h-[480px]"
            }`}
            width={480}
            height={480}
          />
        </div>}
      </div>
    </section>
  );
}
